import { zodResolver } from '@hookform/resolvers/zod';
import { UserRoleEnum, UserRoleEnumZ } from '@monetize/types/app';
import { RoleBasedSettingConfig } from '@monetize/types/experience-config';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import z from 'zod';
import { MBox } from '../../../../../../components/Monetize';
import { UserRoleMultiSelect } from '../../../../../../components/Monetize/Dropdowns/UserRoleMultiSelect';
import { SettingsControl } from '../../../../components/SettingsControl';
import { QuoteAndContractSettingsMetadata } from './quote-settings-experience-config.utils';

const FormSchema = z.object({ values: UserRoleEnumZ.array() });
type Form = z.infer<typeof FormSchema>;

function getDefaultValues(value: RoleBasedSettingConfig): Form {
  if (value.includes('ALL')) {
    return { values: Object.keys(UserRoleEnum) as UserRoleEnum[] };
  }
  return { values: value as UserRoleEnum[] };
}

interface ExperienceConfigRoleBasedSettingProps {
  metadataKey: keyof typeof QuoteAndContractSettingsMetadata;
  value: RoleBasedSettingConfig;
  isLoading?: boolean;
  onSave: (value: RoleBasedSettingConfig) => void;
}

export const ExperienceConfigRoleBasedSetting = ({
  metadataKey,
  value: externalValue,
  isLoading,
  onSave,
}: ExperienceConfigRoleBasedSettingProps) => {
  const {
    control,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm<Form>({
    mode: 'onChange',
    resolver: zodResolver(FormSchema),
    defaultValues: getDefaultValues(externalValue),
  });

  useEffect(() => {
    reset(getDefaultValues(externalValue));
  }, [reset, externalValue]);

  const metadata = QuoteAndContractSettingsMetadata[metadataKey];

  function handleSave({ values }: Form) {
    const payload: RoleBasedSettingConfig =
      values.length === Object.keys(UserRoleEnum).length ? ['ALL'] : values;
    onSave(payload);
  }

  return (
    <Controller
      name={`values`}
      control={control}
      render={({ field: { value, onChange, ...rest } }) => (
        <SettingsControl
          heading={metadata.label}
          subheading={metadata.description}
          control={
            <MBox minW="fit-content">
              <UserRoleMultiSelect
                value={value}
                isCloseButtonLoading={isLoading}
                onChange={(newValue) => {
                  onChange(newValue);
                  handleSubmit(handleSave)();
                }}
              />
            </MBox>
          }
          {...rest}
        />
      )}
    />
  );
};
